import { ExternalLink, Send } from 'lucide-react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../../lib/errors';
import { NAV_PATH, rulesHref } from '../../lib/nav';
import { PlanBody, stateOf, usePlan } from '../../pages/Rules/PlanPanel';
import { PLAN_LABEL, PLAN_T } from '../../pages/Rules/plan-texts';
import { ErrorCodes, errorCode, errorSentence } from '../ErrorToast';
import { Button, IconButton, SidePanel, Skeleton } from '../ui';
import { LABEL, T, say } from './texts';

/*
 * Проект новой версии правил в «Помощнике» (T090; contracts/ui.md §1; FR-033, FR-034; мокап Main.dc.html и
 * Assistant.dc.html, режим «проект»). Состав — панель проекта 005 (`pages/Rules/PlanPanel.tsx`: полоса состояния,
 * сведения, что изменится, влияние, примеры и риски). Публикует человек на странице правил по 005 US5: «Опубликовать
 * проект» ведёт на «Правила расчёта» с этим проектом (`/rules?plan=N`), там — окно публикации 005. В чате публикации нет.
 * - В правой панели — тот же `SidePanel`, что у разговора, в режиме «проект» с кнопкой «К разговору» (`AssistantPanel`).
 * - На странице «Помощник» — правая панель рядом с лентой (`ProjectSidePanel`); на телефоне — на весь экран.
 */

/** Адрес проекта на странице правил. */
export const projectHref = (planId: number) => `${NAV_PATH.rules}?plan=${planId}`;

export interface ProjectView {
  status: ReactNode;
  body: ReactNode;
  footer: ReactNode;
}

/** Части панели проекта: полоса, состав, подвал с «Опубликовать проект». */
export function useProjectView(planId: number, tz: string, onLeave: () => void): ProjectView {
  const navigate = useNavigate();
  const q = usePlan(planId);
  const plan = q.data;
  const go = (to: string) => {
    onLeave();
    navigate(to);
  };
  const status = plan ? stateOf(plan, tz, () => go(rulesHref({ view: 'history' }))) : null;
  let body: ReactNode;
  if (plan) body = <PlanBody plan={plan} draft={null} groups={[]} tzOffset={tz} mayPublish={false} />;
  else if (q.error) {
    const gone = q.error instanceof ApiError && q.error.status === 404;
    const requestId = q.error instanceof ApiError ? q.error.requestId : null;
    const code = errorCode(q.error);
    body = (
      <div role="alert" className="space-y-2 text-[13px]">
        <p className="font-semibold text-ink">{gone ? PLAN_T.notFound : PLAN_LABEL.loadError}</p>
        {!gone && <p className="text-ink-2">{errorSentence(q.error)}</p>}
        {(requestId || code) && (
          <p className="text-[12px] text-ink-2">
            {LABEL.errorCode} <ErrorCodes requestId={requestId} code={code} />
          </p>
        )}
        {!gone && <Button onClick={() => void q.refetch()}>{LABEL.retry}</Button>}
      </div>
    );
  } else
    body = (
      <div aria-busy="true" aria-label={LABEL.loading} className="space-y-3">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  const footer = (
    <>
      <p className="mr-auto min-w-0 flex-1 basis-[200px] text-[12.5px] text-ink-2">{T.projectNote}</p>
      {plan?.state === 'active' && plan.canPublish && (
        <Button
          variant="primary"
          icon={Send}
          className="max-sm:flex-1"
          onClick={() => go(projectHref(planId))}
        >
          {LABEL.publish}
        </Button>
      )}
    </>
  );
  return { status, body, footer };
}

/** Кнопка шапки: тот же проект на странице правил. */
export function OpenOnRules({ planId, onLeave }: { planId: number; onLeave: () => void }) {
  const navigate = useNavigate();
  return (
    <IconButton
      icon={ExternalLink}
      label={LABEL.openOnRules}
      onClick={() => {
        onLeave();
        navigate(projectHref(planId));
      }}
    />
  );
}

/** Проект на странице «Помощник»: правая панель рядом с лентой. */
export function ProjectSidePanel({
  planId,
  tz,
  onClose,
}: {
  planId: number;
  tz: string;
  onClose: () => void;
}) {
  const v = useProjectView(planId, tz, onClose);
  return (
    <SidePanel
      open
      onClose={onClose}
      label={say.project(planId)}
      crumb={LABEL.rulesPage}
      title={say.projectShort(planId)}
      expandable={false}
      actions={<OpenOnRules planId={planId} onLeave={onClose} />}
      status={v.status}
      footer={v.footer}
    >
      <h2 className="text-[16px] font-semibold text-ink">{say.project(planId)}</h2>
      {v.body}
    </SidePanel>
  );
}
